import React from 'react';
import {
  Box,
  Typography,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Theaters,
  Group,
  LocalActivity
} from '@mui/icons-material';

/**
 * FilmAdvancedMetricsPills Component
 * 
 * Displays 7 advanced metrics as compact pill boxes for use in film list views.
 * Extracts calculation logic from AdvancedMetrics component for consistency.
 * 
 * @param {Array} data - Aggregated API response data for a single film
 * @returns {Array} Array of metric objects with calculated values
 */
export const calculateAdvancedMetrics = (data) => {
  if (!data || data.length === 0) {
    return [];
  }

  // Calculate overall totals
  const totals = data.reduce((acc, record) => ({
    totalResponses: acc.totalResponses + (record.TotalResponses || 0),
    totalExcessAwareness: acc.totalExcessAwareness + (record.TotalExcessAwareness || 0),
    totalOutstandingInterest: acc.totalOutstandingInterest + (record.TotalOutstandingInterest || 0),
    totalAwareOpeningWeekend: acc.totalAwareOpeningWeekend + (record.TotalAwareOpeningWeekend || 0),
    totalOutstandingOpeningWeekend: acc.totalOutstandingOpeningWeekend + (record.TotalOutstandingOpeningWeekend || 0),
    totalTheaterAmongAware: acc.totalTheaterAmongAware + (record.TotalTheaterAmongAware || 0),
    totalOutstandingTheater: acc.totalOutstandingTheater + (record.TotalOutstandingTheater || 0),
    totalIntentConversion: acc.totalIntentConversion + (record.TotalIntentConversion || 0),
    totalAware: acc.totalAware + (record.TotalAware || 0),
    totalInterest: acc.totalInterest + (record.TotalInterest || 0)
  }), {
    totalResponses: 0,
    totalExcessAwareness: 0,
    totalOutstandingInterest: 0,
    totalAwareOpeningWeekend: 0,
    totalOutstandingOpeningWeekend: 0,
    totalTheaterAmongAware: 0,
    totalOutstandingTheater: 0,
    totalIntentConversion: 0,
    totalAware: 0,
    totalInterest: 0
  });

  // Define 7 metrics with calculation functions
  const metricDefinitions = [
    {
      id: 'excess-awareness',
      title: 'Excess Awareness',
      subtitle: 'Aware but low interest',
      color: '#f44336',
      icon: <TrendingDown fontSize="small" />,
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const excess = totals.totalExcessAwareness || 0;
        return aware > 0 ? Math.round((excess / aware) * 100) : 0;
      }
    },
    {
      id: 'outstanding-interest',
      title: 'Outstanding Interest',
      subtitle: 'High interest, not aware',
      color: '#ffc107',
      icon: <TrendingUp fontSize="small" />,
      getValue: (totals) => {
        const interest = totals.totalInterest || 0;
        const outstanding = totals.totalOutstandingInterest || 0;
        return interest > 0 ? Math.round((outstanding / interest) * 100) : 0;
      }
    },
    {
      id: 'aware-opening-weekend',
      title: 'Aware Opening Weekend',
      subtitle: 'Aware planning opening',
      color: '#9c27b0',
      icon: <LocalActivity fontSize="small" />,
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const opening = totals.totalAwareOpeningWeekend || 0;
        return aware > 0 ? Math.round((opening / aware) * 100) : 0;
      }
    },
    {
      id: 'outstanding-opening-weekend',
      title: 'Outstanding Opening Weekend',
      subtitle: 'Not aware, planning opening',
      color: '#e91e63',
      icon: <Group fontSize="small" />,
      getValue: (totals) => {
        const responses = totals.totalResponses || 0;
        const outstanding = totals.totalOutstandingOpeningWeekend || 0;
        return responses > 0 ? Math.round((outstanding / responses) * 100) : 0;
      }
    },
    {
      id: 'theater-among-aware',
      title: 'Theater Among Aware',
      subtitle: 'Aware with theater intent',
      color: '#2196f3',
      icon: <Theaters fontSize="small" />,
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const theater = totals.totalTheaterAmongAware || 0;
        return aware > 0 ? Math.round((theater / aware) * 100) : 0;
      }
    },
    {
      id: 'outstanding-theater',
      title: 'Outstanding Theater',
      subtitle: 'Not aware, theater intent',
      color: '#00bcd4',
      icon: <Theaters fontSize="small" />,
      getValue: (totals) => {
        const responses = totals.totalResponses || 0;
        const outstanding = totals.totalOutstandingTheater || 0;
        return responses > 0 ? Math.round((outstanding / responses) * 100) : 0;
      }
    },
    {
      id: 'intent-conversion',
      title: 'Intent Conversion',
      subtitle: 'Aware with viewing intent',
      color: '#388e3c',
      icon: <TrendingUp fontSize="small" />,
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const intent = totals.totalIntentConversion || 0;
        return aware > 0 ? Math.round((intent / aware) * 100) : 0;
      }
    }
  ];

  // Calculate values for each metric
  return metricDefinitions.map(metric => ({
    ...metric,
    value: metric.getValue(totals)
  }));
};

/**
 * FilmAdvancedMetricsPills Component
 * 
 * Renders advanced metrics as compact pill boxes in a grid layout
 * Similar styling to core metrics in FilmTracking page
 */
const FilmAdvancedMetricsPills = ({ data, clampPercentage }) => {
  const metrics = calculateAdvancedMetrics(data);

  if (metrics.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          No advanced metrics data available
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={1.5}>
      {metrics.map((metric) => {
        const value = clampPercentage ? clampPercentage(metric.value) : Math.min(Math.max(metric.value, 0), 100);
        const color = metric.color;
        const displayValue = `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

        return (
          <Grid item xs={12} sm={6} key={metric.id}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: `${color}30`,
                backgroundColor: `${color}08`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ color }}>{metric.icon}</Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {metric.title}
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                {displayValue}
              </Typography>
              {metric.subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {metric.subtitle}
                </Typography>
              )}
              <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: `${color}20`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: color,
                    borderRadius: 2
                  }
                }}
              />
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default FilmAdvancedMetricsPills;
